import React from 'react';
import { Card, Badge, Button, StatusBadge } from '@/components/ui';
import { CardList } from '@/components/ui/CardList';
import { PLAN_CONFIG } from '../types';
import type { Company, SubscriptionPlanCode } from '../types';
import { useNavigate } from 'react-router-dom';
import { AuthButton } from '@/core/permissions/AuthButton';

interface CompaniesMobileListProps {
  companies: Company[];
  onLock: (id: string) => void;
  onUnlock: (id: string) => void;
  onDelete: (id: string) => void;
  onEdit: (company: Company) => void;
  onActivate: (id: string) => void;
  onViewDetail: (id: string) => void;
  onImpersonate: (id: string) => void;
  canDelete: (permission: string, action: string) => boolean;
}

export function CompaniesMobileList({ companies, onLock, onUnlock, onDelete, onEdit, onActivate, onViewDetail, onImpersonate, canDelete }: CompaniesMobileListProps) {
  const navigate = useNavigate();

  return (
    <div className="md:hidden">
      <CardList>
        {companies.map((company) => {
          const planCfg = PLAN_CONFIG[company.subscription_plan_code as SubscriptionPlanCode] || PLAN_CONFIG.free;
          const isLocked = company.is_locked;
          const isActive = company.is_active;

          return (
            <Card
              key={company.id}
              onClick={() => onViewDetail(company.id)}
              className={`p-5 flex flex-col gap-4 relative overflow-hidden group border-none shadow-md ${isLocked ? 'bg-red-50/30' : 'bg-white'}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all ${isLocked ? 'bg-red-100 text-red-600' : 'bg-slate-50 text-slate-400'
                    }`}>
                    <i className={`${isLocked ? 'ri-lock-2-fill' : 'ri-building-4-line'} text-xl`} />
                  </div>
                  <div>
                    <h3 className="sm:text-sm text-xs font-semibold text-slate-900 leading-tight">{company.name}</h3>
                    <p className="text-[10px] text-slate-400 font-mono mt-0.5  tracking-tighter">{company.code}</p>
                  </div>
                </div>
                <Badge variant="custom" className={`${planCfg.badge} font-black text-[9px]  px-2 py-0.5`}>
                  {planCfg.label}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50/50 rounded-xl p-2.5 text-center border border-slate-100">
                  <p className="sm:text-sm text-xs font-semibold text-slate-900">{company.current_projects || 0}</p>
                  <p className="text-[9px] text-slate-400 font-bold ">Dự án</p>
                </div>
                <div className="bg-slate-50/50 rounded-xl p-2.5 text-center border border-slate-100">
                  <p className="sm:text-sm text-xs font-semibold text-slate-900">{company.current_users || 0}</p>
                  <p className="text-[9px] text-slate-400 font-bold ">Users</p>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-50">
                <div className="flex items-center gap-2">
                  <StatusBadge
                    status={isLocked ? 'Bị khóa' : !isActive ? 'Chờ duyệt' : 'Hoạt động'}
                  />
                </div>

                <div className="flex items-center gap-1">
                  {!isActive && !isLocked && (
                    <Button
                      variant="primary"
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/20 px-3 mr-1 h-8 text-[11px]"
                      onClick={(e) => {
                        e.stopPropagation();
                        onActivate(company.id);
                      }}
                    >
                      <i className="ri-checkbox-circle-line mr-1" />
                      Duyệt
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-8 h-8 p-0 text-slate-400"
                    onClick={(e) => { e.stopPropagation(); onViewDetail(company.id); }}
                  >
                    <i className="ri-eye-line" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`w-8 h-8 p-0 ${isLocked ? 'text-emerald-600' : 'text-red-400'}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isLocked) onUnlock(company.id);
                      else onLock(company.id);
                    }}
                  >
                    <i className={isLocked ? "ri-lock-unlock-line" : "ri-lock-2-line"} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-8 h-8 p-0 text-purple-400"
                    title="Impersonate"
                    onClick={(e) => { e.stopPropagation(); onImpersonate(company.id); }}
                  >
                    <i className="ri-spy-fill" />
                  </Button>
                  <AuthButton
                    module="companies"
                    action="edit"
                    variant="ghost"
                    size="sm"
                    className="w-8 h-8 p-0 text-blue-400"
                    onClick={(e) => { e.stopPropagation(); navigate(`/companies/edit/${company.id}`); }}
                  >
                    <i className="ri-edit-line" />
                  </AuthButton>
                </div>
              </div>
            </Card>
          );
        })}
      </CardList>
    </div>
  );
}
